import axios from 'axios';
import Localbase from 'localbase';
import store from '@/store';


// Initialize Localbase
const db = new Localbase('appDatabase');

// Create Axios instance
const api = axios.create({
    baseURL: 'http://127.0.0.1:8000',
});

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Connectivity Check Helper
async function isOffline() {
    try {
        await axios.get('http://127.0.0.1:8000/api/test', { timeout: 3000 });
        console.log('API Reached');
        return false; // Return false if online
    } catch (error) {
        console.log('API failed', error);
        return true; // Return true if offline
    }
}

function generateUniqueId() {
    const randomPart = Math.random().toString(16).substr(2, 4); // 4 random hex characters
    const timePart = Date.now().toString(16).slice(-4); // Last 4 hex characters of timestamp
    return randomPart + timePart;
}

function getBaseUrl(url) {
    // Remove the last alphanumeric segment (including optional trailing slash)
    return url.replace(/\/[a-zA-Z0-9]+\/?$/, ''); // Replace `/alphanumeric` or `/alphanumeric/` at the end of the string
}

async function getPendingTaskAssessment(logbookId, traineeId, attemptNo) {
    const cacheKey = '/api/task_assessments';
    const cachedDoc = await db.collection('cache').doc(cacheKey).get();

    if (cachedDoc && Array.isArray(cachedDoc.data)) {
        for (const entry of cachedDoc.data) {
            if (
                parseInt(entry.logbook_id) === parseInt(logbookId) &&
                parseInt(entry.trainee_id) === parseInt(traineeId) &&
                parseInt(entry.attempt_no) === parseInt(attemptNo)
            ) {
                return {
                    ...entry,
                    pending: true, // Mark as locally cached/pending
                };
            }
        }
    }

    return null; // No match found
}

async function updateEntryCacheWithPendingRequest(pendingRequest) {
    try {
        const { url, method, data } = pendingRequest;
        const requestData = JSON.parse(data);

        let logbookId = null;
        let updates = [];

        // Handle batch update
        if (url.includes('/api/logbookentry/batch-update')) {
            if (!Array.isArray(requestData.logbook_entries)) {
                console.error('Invalid batch update payload.');
                return;
            }

            updates = requestData.logbook_entries;
            logbookId = updates[0]?.logbook_id;

            if (!logbookId) {
                console.error('Logbook ID missing in batch request.');
                return;
            }

            // Handle single-task PUT
        } else if (method === 'put' && /\/api\/logbookentry\/\d+$/.test(url)) {
            const taskIdMatch = url.match(/\/(\d+)$/);
            const taskId = taskIdMatch ? parseInt(taskIdMatch[1]) : null;
            if (!taskId) {
                console.error('Task ID not found in URL.');
                return;
            }

            logbookId = requestData.logbook_id;
            if (!logbookId) {
                console.error('Logbook ID missing in request data.');
                return;
            }

            updates = [{
                task_id: taskId,
                ...requestData
            }];
        } else {
            console.error('Unsupported request format for cache update.');
            return;
        }

        const cacheKey = `/api/logbookentry/${logbookId}`;
        const existingCache = await db.collection('cache').doc(cacheKey).get();

        if (!existingCache || !existingCache.data || !Array.isArray(existingCache.data.tasks)) {
            console.error('No valid cache or tasks array found.');
            return;
        }

        const updatedTasks = [...existingCache.data.tasks];

        for (const update of updates) {
            const taskIndex = updatedTasks.findIndex(task => task.task_id === update.task_id);

            if (taskIndex > -1) {
                updatedTasks[taskIndex] = {
                    ...updatedTasks[taskIndex],
                    ...update,
                    pending: true,
                };
            } else {
                console.warn(`Task with task_id ${update.task_id} not found in cache.`);
                updatedTasks.push({
                    task_id: update.task_id,
                    ...update,
                    pending: true,
                });
            }
        }

        await db.collection('cache').doc(cacheKey).update({
            data: {
                ...existingCache.data,
                tasks: updatedTasks,
            },
            timestamp: new Date().getTime(),
        });

        console.log(`Updated tasks in logbook_id ${logbookId} cache.`);
    } catch (error) {
        console.error('Error updating cache with pending request:', error);
    }
}

async function updateCacheWithPendingRequest(pendingRequest) {
    try {
        const cacheKey = getBaseUrl(pendingRequest.url);
        console.log('cacheKey', cacheKey);
        const existingCache = await db.collection('cache').doc(cacheKey).get();

        if (!existingCache || !Array.isArray(existingCache.data)) {
            console.error('No valid cache found for the given key.');
            return;
        }

        const updatedData = [...existingCache.data];
        const requestData = JSON.parse(pendingRequest.data);

        // Function to convert date format
        const formatDate = (dateString) => {
            if (!dateString) return dateString;
            const date = new Date(dateString);
            if (isNaN(date)) return dateString; // Return as is if it's not a valid date

            return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour12: false });
        };

        // Iterate over requestData and format any date fields
        Object.keys(requestData).forEach(key => {
            if (key.toLowerCase().includes('date') || key.toLowerCase().includes('sign_off')) {
                requestData[key] = formatDate(requestData[key]);
            }
        });

        // Try to match the item ID based on the URL
        const idMatch = pendingRequest.url.match(/\/(\d+)(\/|$)/); // First, try to match a numeric ID
        let itemId;

        if (idMatch) {
            itemId = parseInt(idMatch[1], 10) // Use the numeric ID if found
            console.log('number itemId', itemId);
        } else {
            // If no numeric ID is found, try to match an alphanumeric ID
            const alphaNumMatch = pendingRequest.url.match(/\/([a-zA-Z0-9]+)(?=\/?$)/);
            itemId = alphaNumMatch ? alphaNumMatch[1] : pendingRequest.id;
        }

        console.log('itemId', itemId);

        if (!itemId) {
            console.error('Unable to extract ID from the URL or request data:', pendingRequest.url);
            return;
        }

        // Extract primary key dynamically from the existing cache
        const extractPrimaryKey = (data, url) => {
            if (url.includes('task/assessments')) {
                return 'id';
            }
            if (Array.isArray(data) && data.length > 0) {
                const firstItem = data[0];
                return Object.keys(firstItem).find(key => key.includes('_id')) || 'id'; // Default to 'id'
            }
            return 'id'; // Fallback if no key is found
        };
        const primaryKey = extractPrimaryKey(existingCache.data, pendingRequest.url);

        // Update the corresponding item in the cache
        const itemIndex = updatedData.findIndex(item => item[primaryKey] === itemId);
        if (itemIndex > -1) {
            updatedData[itemIndex] = {
                ...updatedData[itemIndex],
                ...requestData, // Merge the pendingRequest data
                pending: true,  // Mark it as pending
            };
        } else {
            // Add a new entry if not found (unlikely for PUT, but just in case)
            updatedData.push({
                ...requestData,
                [primaryKey]: itemId,
                pending: true,
            });
        }

        // Save the updated cache back to Localbase
        await db.collection('cache').doc(cacheKey).update({
            data: updatedData,
            timestamp: new Date().getTime(),
        });

        console.log('Cache updated temporarily with pending request.');
    } catch (error) {
        console.error('Error updating cache with pending request:', error);
    }
}

api.interceptors.response.use(
    async (response) => {
        // Exclude /api/login from caching
        if (response.config.url === '/api/login') {
            return response; // Just return the response without caching
        }

        const cacheKey = response.config.url;

        if (response.config.method === 'get') {
            // Save full GET response to Localbase
            await db.collection('cache').add(
                {
                    key: cacheKey,
                    data: response.data,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    timestamp: new Date().getTime(),
                },
                cacheKey
            );
        }
        return response;
    },
    async (error) => {
        const { method, url, data, headers } = error.config;

        if (await isOffline()) {
            const offlineError = {
                isOffline: true,
                message: "You are offline. The request has been cached and will sync later.",
            };

            store.commit('setOnlineStatus', false);

            if (url !== '/api/login' && ['post', 'put', 'delete'].includes(method)) {
                const requestData = {
                    method,
                    url,
                    data,
                    headers,
                    timestamp: new Date().getTime(),
                };

                // Save the request to pendingRequests
                const docRef = await db.collection('pendingRequests').add(requestData);
                requestData.key = docRef.data.key;

                if (method === 'post' && url === '/api/logbookentry_completions/batch-update') {
                    await updateCacheWithBatchRequest(requestData);
                }

                // Temporarily update the cache for PUT requests
                if (method === 'put' && (url.includes('/api/logbookentry/')) || method === 'post' && (url.includes('/api/logbookentry/batch-update'))) {
                    await updateEntryCacheWithPendingRequest(requestData);
                }

                // Temporarily update the cache for PUT requests
                if (method === 'put') {
                    await updateCacheWithPendingRequest(requestData);
                }

                const uid = generateUniqueId();

                // Save the request to pendingRequests with the generated primaryKey
                requestData.id = uid;

                const cacheKey = url;
                const existingCache = await db.collection('cache').doc(cacheKey).get();
                let updatedData = [];

                const extractPrimaryKey = (data) => {
                    if (url.includes('task/assessments')) {
                        return 'id';
                    }
                    if (Array.isArray(data) && data.length > 0) {
                        const firstItem = data[0];
                        const primaryKey = Object.keys(firstItem).find(key => key.includes('_id')) || 'id';
                        return primaryKey;
                    }
                    return 'id';  // Fallback if no key is found
                };

                let primaryKey; // Declare `primaryKey` to use it later in requestData            

                if (existingCache && Array.isArray(existingCache.data)) {
                    primaryKey = extractPrimaryKey(existingCache.data);
                    console.log('existing cache primaryKey', primaryKey);

                    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

                    // Ensure the id is explicitly added to the data itself
                    const newEntry = {
                        ...parsedData,
                        id: uid,
                        pending: true,
                    };

                    updatedData = [...existingCache.data, newEntry];

                    await db.collection('cache').doc(cacheKey).update({
                        data: updatedData,
                        pending: true,
                        timestamp: new Date().getTime(),
                    });
                }

                // Update pendingRequests with the modified requestData
                await db.collection('pendingRequests').doc(docRef.data.key).update(requestData);
                window.alert("You are offline. Your request has been cached and changes are reflected locally.");
            }

            if (method === 'get') {
                const cachedResponse = await db.collection('cache').doc(url).get();
                if (cachedResponse) {
                    const { data, timestamp, ...rest } = cachedResponse;
                    const cacheValidity = 30 * 24 * 60 * 60 * 1000;

                    if (new Date().getTime() - timestamp < cacheValidity) {
                        return Promise.resolve({
                            data,
                            ...rest,
                        });
                    }
                }

                // 👇 Check pendingRequests manually
                const match = url.match(/\/api\/task_assessments\/(\d+)\/(\d+)\/(\d+)/);
                if (match) {
                    const [, logbookId, traineeId, attemptNo] = match;
                    const pendingData = await getPendingTaskAssessment(logbookId, traineeId, attemptNo);
                    if (pendingData) {
                        return Promise.resolve({
                            data: pendingData,
                            status: 200,
                            statusText: 'OK',
                            headers: {},
                            config: error.config,
                        });
                    }
                }

                window.alert("You are offline and the app could not find the data in cache or pending requests.");
            }

            return Promise.reject(offlineError);
        }

        return Promise.reject(error);
    }
);

// Retry pending requests when the app is back online
async function retryPendingRequests() {
    const idMapping = {}; // Temporary mapping of uuid -> real id
    const extractPrimaryKey = (data, url) => {
        if (url.includes('task/assessments')) {
            return 'id';
        }
        if (Array.isArray(data) && data.length > 0) {
            const firstItem = data[0];
            const primaryKey = Object.keys(firstItem).find(key => key.includes('_id')) || 'id';
            return primaryKey;
        }
        return 'id'; // Fallback if no key is found
    };
    try {
        const pendingRequests = await db.collection('pendingRequests').get();

        if (pendingRequests.length === 0) {
            console.log('No pending requests to retry.');
            return;
        }
        store.commit('setSyncing', true);
        store.commit('setSyncStatus', 'Syncing data...');

        let allFailed = true;

        for (const request of pendingRequests) {
            try {
                // Update the URL for PUT requests
                if (request.method === 'put' || request.method === 'delete') {
                    const urlSegments = request.url.split('/');
                    const updatedSegments = urlSegments.map(segment =>
                        idMapping[segment] || segment // Replace temporaryId with realId if it exists in the mapping
                    );
                    request.url = updatedSegments.join('/');
                    console.log('url', request.url);
                }

                // Retry the request
                const response = await api({
                    method: request.method,
                    url: request.url,
                    data: request.data,
                    headers: request.headers,
                });
                console.log('request', request);
                console.log('response', response);

                // If it's a POST, store the mapping
                if (request.method === 'post') {
                    const primaryKey = extractPrimaryKey([response.data], request.url);
                    console.log('primaryKey', primaryKey);
                    const temporaryId = request.id;
                    console.log('temporaryId', temporaryId);
                    const realId = response.data[primaryKey]; // Adjust based on your API's response format
                    console.log('real id', realId);
                    idMapping[temporaryId] = realId;
                    console.log('mapping', idMapping);
                }
                if (request.method === 'put') {
                    const cacheKey = getBaseUrl(request.url);
                    const existingCache = await db.collection('cache').doc(cacheKey).get();
                    const primaryKey = extractPrimaryKey([response.data], request.url);
                    const realId = response.data[primaryKey];

                    if (existingCache && Array.isArray(existingCache.data)) {
                        const updatedData = existingCache.data.map(item => {
                            if (item[primaryKey] === realId) {
                                return { ...item, ...request.data, pending: false }; // Replace temp data with server data
                            }
                            return item;
                        });

                        await db.collection('cache').doc(cacheKey).update({
                            data: updatedData,
                            timestamp: new Date().getTime(),
                        });

                        console.log('Cache synced with server data.');
                    }
                }

                console.log(`Retried ${request.method.toUpperCase()} request successful: ${request.url}`, response);

                await db.collection('pendingRequests').doc(request.key).delete();
                console.log('Successfully deleted pending request from DB.');

                store.commit('setSyncStatus', `Synced request: ${request.key}`);
                allFailed = false;
            } catch (retryError) {
                console.error('Retry failed for request:', request.key, retryError);
                store.commit('setSyncStatus', `Failed to sync request: ${request.key}`);
            }
            await wait(1500);
        }

        if (allFailed) {
            window.alert('Syncing failed.');
            store.commit('setSyncStatus', 'Syncing failed.');
        } else {
            store.commit('setSyncStatus', 'Data sync completed.');
        }

        await wait(2000);
        //window.location.reload(); // Reload the page to reflect the changes
    } catch (error) {
        console.error('Syncing failed:', error);
        store.commit('setSyncStatus', 'Syncing failed.');
        window.alert('Syncing process encountered an error. Please try again.');
    } finally {
        setTimeout(() => {
            store.commit('setSyncing', false);
        }, 2000);
    }
}

//////////////////////////////////////////////////////////////////////////////

async function updateCacheWithBatchRequest(pendingRequest) {
    try {
        const cacheKey = '/api/logbookentry_completions';
        console.log('Updating cache with batch request:', pendingRequest);

        // Retrieve existing cache
        let existingCache = await db.collection('cache').doc(cacheKey).get();
        if (!existingCache || !Array.isArray(existingCache.data)) {
            existingCache = { data: [] }; // Initialize if no cache exists
        }

        let updatedData = [...existingCache.data];
        const requestData = JSON.parse(pendingRequest.data);

        // Generate temp ID (for pending local entries only)
        const generateTempId = () => 'temp-' + Math.random().toString(16).substr(2, 8);

        requestData.completions.forEach(entry => {
            updatedData.push({
                ...entry,
                id: generateTempId(), // Use a temp `id`, not logbookentry_id
                pending: true,
            });
        });

        await db.collection('cache').doc(cacheKey).update({
            data: updatedData,
            timestamp: new Date().getTime(),
        });

        console.log('Batch data temporarily added to cache:', updatedData);
    } catch (error) {
        console.error('Error updating cache with batch request:', error);
    }
}


////////////////////////////////////////////////////////////////////////////////

// Check if the app is back online when the script runs
(async () => {
    const onlineStatus = await isOffline();
    if (!onlineStatus) {
        retryPendingRequests(); // Retry pending requests if online
    }
})();

export default api;
